import { useState, useEffect, useCallback } from 'react';
import { CornerUpRight, ArrowLeft } from 'lucide-react';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { rpc, RpcError } from '@/api/client';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { ToolHeader } from '@/components/common/ToolHeader';
import { SessionSelector } from '@/components/sector/SessionSelector';
import { WizardStepper } from '@/components/sector/WizardStepper';
import { SessionRemovedAnalysisDialog } from '@/components/analysis/SessionRemovedAnalysisDialog';
import { useWizardState } from '@/hooks/useWizardState';
import type {
  SessionSummary,
  ListSessionsResponse,
  ListAnalysisTracksResponse,
  SessionDetailResponse,
  AnalyzeCornerResponse,
  CornerData,
  AnalyzeResponse,
  GetAnalysisResponse,
  AnalysisRecord,
} from '@/types/telemetry';
import type { TrackShape, TrackAnnotation } from '@/lib/spline';
import { CornerMap } from './CornerMap';
import { CornerStatsCard } from './CornerStatsCard';
import { AllCornersOverview } from './AllCornersOverview';
import { CornerPhaseTabs, type CornerPhase } from './CornerPhaseTabs';
import { CornerLapTable } from './CornerLapTable';
import { CornerMetricTabs, type CornerMetric } from './CornerMetricTabs';

interface CornerAnalysisProps {
  trackName?: string;
}

export function CornerAnalysis({ trackName }: CornerAnalysisProps) {
  const { navigate } = useRouter();
  const { t } = useI18n();
  const wizardSteps = [t('corner.wizardChooseTrack'), t('corner.wizardSelect'), t('corner.wizardResults')];

  const [wizardState, updateWizardState] = useWizardState({
    step: trackName ? 1 : 0,
    draftTrack: undefined as string | undefined,
    selectedIds: [] as string[],
    driverFilter: 'all',
    weatherFilter: 'all',
    activeCorner: 0,
    activePhase: 'entrance' as CornerPhase,
    activeMetric: 'speed' as CornerMetric,
  });

  const { step, draftTrack, selectedIds: selectedIdsArr, driverFilter, weatherFilter, activeCorner, activePhase, activeMetric } = wizardState;
  const selectedIds = new Set(selectedIdsArr);

  const setStep = useCallback((newStep: number | ((prev: number) => number)) => {
    updateWizardState(prev => {
      const nextStep = typeof newStep === 'function' ? newStep(prev.step) : newStep;
      return { step: nextStep };
    }, false);
  }, [updateWizardState]);

  const setDraftTrack = useCallback((track: string | undefined) => updateWizardState({ draftTrack: track }), [updateWizardState]);
  const setSelectedIds = useCallback((ids: Set<string> | ((prev: Set<string>) => Set<string>)) => {
    updateWizardState(prev => {
      const nextIds = typeof ids === 'function' ? ids(new Set(prev.selectedIds)) : ids;
      return { selectedIds: [...nextIds] };
    });
  }, [updateWizardState]);
  const setDriverFilter = useCallback((filter: string) => updateWizardState({ driverFilter: filter }), [updateWizardState]);
  const setWeatherFilter = useCallback((filter: string) => updateWizardState({ weatherFilter: filter }), [updateWizardState]);
  const setActiveCorner = useCallback((corner: number) => updateWizardState({ activeCorner: corner }), [updateWizardState]);
  const setActivePhase = useCallback((phase: CornerPhase) => updateWizardState({ activePhase: phase }), [updateWizardState]);
  const setActiveMetric = useCallback((metric: CornerMetric) => updateWizardState({ activeMetric: metric }), [updateWizardState]);

  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [availableTracks, setAvailableTracks] = useState<string[]>([]);
  const [trackId, setTrackId] = useState('');

  const [analysisResult, setAnalysisResult] = useState<AnalyzeCornerResponse | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState<string | undefined>(draftTrack || trackName || undefined);
  const [loading, setLoading] = useState(!trackName);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [sessionRemovedAnalysis, setSessionRemovedAnalysis] = useState<AnalysisRecord | null>(null);

  // Track map geometry
  const [trackShape, setTrackShape] = useState<TrackShape | null>(null);
  const [trackAnnotations, setTrackAnnotations] = useState<TrackAnnotation[]>([]);

  const handleLoadAnalysis = useCallback(async (analysis: AnalysisRecord) => {
    const cor = analysis.corner;
    if (cor?.track) {
      const tName = cor.track.trackName || cor.track.trackId;
      setSelectedTrack(tName);
      setTrackId(cor.track.trackId);
    }
    if (cor?.sessions?.sessionIds) {
      setSelectedIds(new Set(cor.sessions.sessionIds));
    }
    setAnalyzing(true);
    setError(null);
    try {
      if (analysis.id) {
        const resp = await rpc<GetAnalysisResponse>('GetAnalysis', { id: analysis.id });
        if (resp.corner) {
          setAnalysisResult(resp.corner);
          setActiveCorner(0);
          setStep(2);
        }
      } else {
        const resp = await rpc<AnalyzeResponse>('Analyze', {
          corner: cor,
          skipSave: true,
        });
        if (resp.corner) {
          setAnalysisResult(resp.corner);
          setActiveCorner(0);
          setStep(2);
        }
      }
    } catch (err: any) {
      console.error('Failed to evaluate loaded corner analysis:', err);
      const isSessionMissing = err?.status === 404 || err?.isNotFound || (err?.message && (err.message.includes('session') || err.message.includes('not found')));
      if (isSessionMissing) {
        setSessionRemovedAnalysis(analysis);
      } else {
        setError(err.message || 'Failed to analyze corner speeds');
      }
    } finally {
      setAnalyzing(false);
    }
  }, [setStep, setSelectedIds, setActiveCorner]);

  // Handle restoring analysis from history navigation
  useEffect(() => {
    const stored = sessionStorage.getItem('loaded_analysis_record');
    if (stored) {
      sessionStorage.removeItem('loaded_analysis_record');
      try {
        const rec = JSON.parse(stored) as AnalysisRecord;
        if (rec.type === 'ANALYSIS_TYPE_CORNER') {
          handleLoadAnalysis(rec);
        }
      } catch {}
    }
  }, [handleLoadAnalysis]);

  // Fetch available tracks (strictly filtering for tracks with shapes and corners)
  useEffect(() => {
    let cancelled = false;
    rpc<ListAnalysisTracksResponse>('ListAnalysisTracks', { requireCorners: true }).then(resp => {
      if (!cancelled) {
        setAvailableTracks(resp.tracks || []);
      }
    }).catch(console.error);
    return () => { cancelled = true; };
  }, []);

  // Fetch sessions and track shape for selected track
  useEffect(() => {
    if (!selectedTrack) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const listResp = await rpc<ListSessionsResponse>('ListSessions', { pageSize: 100, track_name: selectedTrack });
        const filteredSessions = listResp.sessions || [];
        if (filteredSessions.length === 0) {
          setError(`${t('corner.noSessionsForTrack')} "${selectedTrack}"`);
          setLoading(false);
          return;
        }

        const detail = await rpc<SessionDetailResponse>('GetSession', { sessionId: filteredSessions[0].id });
        const tId = detail.session.trackId;

        try {
          const shapeResp = await rpc<{ shape: TrackShape | null; annotations: TrackAnnotation[] }>('GetTrackShapeAndAnnotations', { trackId: tId });
          if (shapeResp && !cancelled) {
            setTrackShape(shapeResp.shape || null);
            setTrackAnnotations(shapeResp.annotations || []);
          }
        } catch (err) {
          console.warn('Track shape not available:', err);
        }

        if (cancelled) return;

        setSessions(filteredSessions);
        setTrackId(tId);

        // Pre-select first session if none selected
        if (selectedIds.size === 0 && filteredSessions.length > 0) {
          updateWizardState({
            selectedIds: [filteredSessions[0].id],
            driverFilter: filteredSessions[0].driverName || 'all',
          });
        }
      } catch (e: any) {
        if (!cancelled) {
          const nf = e instanceof RpcError && e.isNotFound;
          setNotFound(nf);
          setError(nf ? 'The requested resource could not be found.' : (e.message || 'Failed to load data'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTrack]);

  const handleAnalyze = useCallback(async (options?: { skipSave?: boolean }) => {
    if (selectedIds.size === 0 || !trackId) return;
    setAnalyzing(true);
    setError(null);
    try {
      const resp = await rpc<AnalyzeResponse>('Analyze', {
        skipSave: options?.skipSave,
        corner: {
          track: { trackId, trackName: selectedTrack },
          sessions: { sessionIds: [...selectedIds] },
          corners: {},
        },
      });
      if (resp.corner) {
        setAnalysisResult(resp.corner);
        setActiveCorner(0);
        setStep(2);
      }
    } catch (e: any) {
      console.error(e);
      setError(e.message || 'Failed to analyze corner speeds');
    } finally {
      setAnalyzing(false);
    }
  }, [trackId, selectedTrack, selectedIds, setActiveCorner, setStep]);

  const handleStepClick = useCallback((targetStep: number) => {
    if (targetStep < step && selectedTrack) {
      setStep(targetStep);
    }
  }, [step, selectedTrack, setStep]);

  // Handle deep-linking into step 2 (e.g. on page refresh or direct URL navigation)
  useEffect(() => {
    if (step === 2 && !analysisResult && selectedIds.size > 0 && !analyzing && trackId) {
      handleAnalyze({ skipSave: true });
    }
  }, [step, analysisResult, selectedIds.size, analyzing, handleAnalyze, trackId]);

  const uniqueDrivers = [...new Set(sessions.map(s => s.driverName || '').filter(Boolean))].sort();

  const corners = (analysisResult?.corners || []).map((c, idx) => ({
    ...c,
    orderIndex: idx + 1,
  }));
  const currentCorner = corners[activeCorner] || corners[0] || null;

  if (loading) return <LoadingSpinner />;
  if (error && step !== 2) return <ErrorState message={error} isNotFound={notFound} />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <ToolHeader
        title={t('corner.title')}
        icon={CornerUpRight}
      />

      {/* Wizard stepper */}
      <div className="flex justify-center mb-6">
        <WizardStepper
          steps={wizardSteps}
          currentStep={step}
          onStepClick={handleStepClick}
        />
      </div>

      {/* Step 0: Choose Track */}
      {step === 0 && (
        <div className="max-w-2xl mx-auto">
          <div className="card overflow-hidden">
            <div className="px-4 py-3 border-b border-border-subtle">
              <h2 className="text-sm font-semibold text-text-primary">{t('corner.wizardChooseTrack')}</h2>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {availableTracks.length > 0 ? (
                availableTracks.map(track => {
                  const isSelected = draftTrack === track;
                  return (
                    <div
                      key={track}
                      onClick={() => setDraftTrack(track)}
                      className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors border-b border-border-subtle last:border-0 group ${
                        isSelected ? 'bg-accent/10 border-l-2 border-l-accent' : 'hover:bg-bg-hover'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          checked={isSelected}
                          onChange={() => setDraftTrack(track)}
                          className="w-3.5 h-3.5 accent-accent cursor-pointer"
                        />
                        <span className="text-sm font-medium text-text-primary">{track}</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-8 text-center text-text-muted text-sm">
                  {t('corner.noTracksWithCorners')}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-border-subtle flex justify-end">
              <button
                disabled={!draftTrack}
                onClick={() => {
                  if (draftTrack) {
                    setSelectedTrack(draftTrack);
                    setStep(1);
                  }
                }}
                className={`
                  px-4 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer
                  ${draftTrack ? 'bg-accent text-white hover:bg-accent-hover' : 'bg-bg-surface text-text-muted cursor-not-allowed border border-border-subtle'}
                `}
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Select Sessions */}
      {step === 1 && (
        <div className="max-w-2xl mx-auto flex flex-col gap-4">
          <SessionSelector
            sessions={sessions}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            driverFilter={driverFilter}
            onDriverFilterChange={setDriverFilter}
            weatherFilter={weatherFilter}
            onWeatherFilterChange={setWeatherFilter}
            uniqueDrivers={uniqueDrivers}
          />

          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setStep(0)}
              className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors cursor-pointer"
            >
              <ArrowLeft size={14} />
              {t('common.back')}
            </button>

            <button
              disabled={selectedIds.size === 0 || analyzing}
              onClick={handleAnalyze}
              className={`
                flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer
                ${selectedIds.size > 0 && !analyzing ? 'bg-accent text-white hover:bg-accent-hover' : 'bg-bg-surface text-text-muted cursor-not-allowed border border-border-subtle'}
              `}
            >
              {analyzing ? <LoadingSpinner size="sm" /> : <CornerUpRight size={14} />}
              <span>{analyzing ? t('common.loading') : t('corner.title')}</span>
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Corner Speed Analysis Results */}
      {step === 2 && (
        <div className="flex flex-col gap-6">
          {error && <ErrorState message={error} />}

          {corners.length > 0 && currentCorner ? (
            <>
              {/* Metric Selector Tabs */}
              <CornerMetricTabs
                activeMetric={activeMetric}
                onMetricChange={setActiveMetric}
              />

              {/* Track Map at the top of the screen */}
              {analysisResult?.referenceTrack && (
                <div className="w-full max-w-xl mx-auto flex flex-col items-center gap-3">
                  <div className="text-center">
                    <span className="text-xs font-semibold uppercase tracking-widest text-text-secondary">
                      {selectedTrack} · {corners.length} {t('corner.turn')}s
                    </span>
                  </div>

                  <div className="w-full">
                    <CornerMap
                      samples={analysisResult.referenceTrack}
                      corners={corners}
                      selectedCornerIndex={activeCorner}
                      onSelectCorner={setActiveCorner}
                      trackShape={trackShape}
                      trackAnnotations={trackAnnotations}
                    />
                  </div>

                  {/* Floating Legend below map */}
                  <div className="flex items-center justify-center gap-4 sm:gap-6 py-2 px-5 rounded-full bg-bg-raised/80 backdrop-blur-md border border-border-subtle/80 shadow-lg text-xs mx-auto max-w-fit flex-wrap">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-2.5 rounded-xs bg-yellow-400/40 border border-yellow-400" />
                      <span className="text-[10px] text-text-muted uppercase tracking-wider">{t('corner.turn')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3.5 h-3.5 rounded-full bg-yellow-400 border border-white flex items-center justify-center text-[8px] font-bold text-slate-900 shadow-xs">
                        T
                      </div>
                      <span className="text-[10px] text-text-muted uppercase tracking-wider">{t('corner.apex')}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Active Corner Summary Cards */}
              <CornerStatsCard corner={currentCorner} activeMetric={activeMetric} />

              {/* Phase Selector Tabs */}
              <CornerPhaseTabs
                activePhase={activePhase}
                onPhaseChange={setActivePhase}
                activeMetric={activeMetric}
              />

              {/* Per-Lap Speed/RPM/Duration Table */}
              <CornerLapTable
                corner={currentCorner}
                activePhase={activePhase}
                activeMetric={activeMetric}
              />

              {/* All Corners Overview Comparison Table */}
              <AllCornersOverview
                corners={corners}
                selectedCornerIndex={activeCorner}
                onSelectCorner={setActiveCorner}
                activeMetric={activeMetric}
              />
            </>
          ) : !analyzing && (
            <div className="card p-12 text-center text-text-muted text-sm">
              {t('corner.noCornersFound')}
            </div>
          )}
        </div>
      )}

      {sessionRemovedAnalysis && (
        <SessionRemovedAnalysisDialog
          open={!!sessionRemovedAnalysis}
          onClose={() => {
            setSessionRemovedAnalysis(null);
            navigate('#/analysis/history');
          }}
          analysis={sessionRemovedAnalysis}
          onDeleted={() => {
            setSessionRemovedAnalysis(null);
            navigate('#/analysis/history');
          }}
        />
      )}
    </div>
  );
}
